import {
  isNullOrWhiteSpace,
  resolveActionRequired,
} from "../utils/reportHelpers";

export function buildActionPlanItems(reportData) {
  const hideWhenNonCompliant = [
    "Any Visual defects on the door?",
    "Any damage to the door?",
    "Any damage to the frame?",
  ];
  const showWhenCompliant = [
    "Any Visual defects on the door?",
    "Any damage to the door?",
    "Does the door fit correctly within the frame?",
    "Any damage to the frame?",
  ];

  const actionItems = [];
  const inspectedDoorsList =
    reportData?.propertyInspectionReport?.inspectedDoors ?? [];
  const physicalMeasurementsList =
    reportData?.propertyInspectionReport?.physicalMeasurements ?? [];
  const complianceChecksList =
    reportData?.propertyInspectionReport?.complianceChecks ?? [];

  inspectedDoorsList.forEach((door) => {
    const pm = physicalMeasurementsList.find(
      (x) => x.doorId === door?.inspectedDoorId,
    );
    if (pm != null) {
      const parts = [
        { title: "Head", item: pm?.head },
        { title: "Hinge", item: pm?.hinge },
        { title: "Closing", item: pm?.closing },
        { title: "Threshold", item: pm?.threshold },
        { title: "DoorThickness", item: pm?.doorThickness },
        { title: "FrameDepth", item: pm?.frameDepth },
        { title: "DoorSize", item: pm?.doorSize },
        { title: "FullDoorsetSize", item: pm?.fullDoorsetSize },
        { title: "NoOfHinges", item: pm?.noOfHinges },
        { title: "LeftHead", item: pm?.leftHead },
        { title: "RightHead", item: pm?.rightHead },
        { title: "LeftHinge", item: pm?.leftHinge },
        { title: "RightHinge", item: pm?.rightHinge },
        { title: "LeftThreshold", item: pm?.leftThreshold },
        { title: "RightThreshold", item: pm?.rightThreshold },
      ];

      parts.forEach((p) => {
        const item = p.item;
        const isDoubleDoor =
          p.title === "LeftHead" ||
          p.title === "RightHead" ||
          p.title === "LeftHinge" ||
          p.title === "RightHinge" ||
          p.title === "LeftThreshold" ||
          p.title === "RightThreshold";

        if ((item?.actionItem || "").toLowerCase() !== "yes") return;
        if (isDoubleDoor && !(item?.severity && item.severity.trim())) return;

        const severity =
          item?.severity && item.severity.trim() ? item.severity : "Low";
        const actionText = resolveActionRequired(
          item?.remediation,
          item?.comment,
          item?.category,
          item?.severity,
        );

        if (actionText && actionText.trim()) {
          actionItems.push({
            door,
            title: p.title,
            severity,
            actionRequired: actionText,
            remediation: item?.remediation,
            comment: item?.comment,
            dueDate: item?.dueDate,
            completedOn: item?.completedOn,
            completedBy: item?.completedBy || "",
            assignedTo: item?.assignedTo || "",
          });
        }
      });

      if (!isNullOrWhiteSpace(pm?.customComplianceActionJson)) {
        const jsonString = pm.customComplianceActionJson.trim();
        if (
          !isNullOrWhiteSpace(jsonString) &&
          jsonString !== "[]" &&
          jsonString !== "null" &&
          jsonString.startsWith("[")
        ) {
          try {
            const customAction = JSON.parse(jsonString);
            if (Array.isArray(customAction) && customAction.length > 0) {
              customAction.forEach((customActionElement) => {
                try {
                  const actionItem = customActionElement?.ActionItem ?? "";
                  if (
                    isNullOrWhiteSpace(actionItem) ||
                    actionItem.toLowerCase() !== "yes"
                  ) {
                    return;
                  }
                  if (
                    isNullOrWhiteSpace(customActionElement?.Severity ?? "")
                  ) {
                    return;
                  }
                  const severity = customActionElement.Severity;
                  const actionText = !isNullOrWhiteSpace(
                    customActionElement?.Remediation ?? "",
                  )
                    ? customActionElement.Remediation
                    : !isNullOrWhiteSpace(customActionElement?.Comment ?? "")
                      ? customActionElement.comment
                      : customActionElement?.CustomLabel ?? "";

                  let dueDate = null;
                  if (
                    customActionElement?.DueDate &&
                    typeof customActionElement.DueDate === "string" &&
                    customActionElement.DueDate.trim() !== ""
                  ) {
                    const parsed = new Date(customActionElement.DueDate);
                    if (!isNaN(parsed.getTime())) dueDate = parsed;
                  }

                  let completedDate = null;
                  if (
                    customActionElement?.CompletedOn &&
                    typeof customActionElement.CompletedOn === "string" &&
                    customActionElement.CompletedOn.trim() !== ""
                  ) {
                    const parsed = new Date(customActionElement.CompletedOn);
                    if (!isNaN(parsed.getTime())) completedDate = parsed;
                  }

                  actionItems.push({
                    door,
                    title: customActionElement?.CustomLabel ?? "Custom Action",
                    severity,
                    actionRequired: actionText,
                    remediation: customActionElement?.remediation,
                    comment: customActionElement?.comment,
                    dueDate,
                    completedOn: completedDate,
                    completedBy:
                      customActionElement?.CompletedBy ||
                      customActionElement?.completedBy ||
                      "",
                    assignedTo:
                      customActionElement?.ActionItemAssignedTo || "",
                  });
                } catch {
                  /* skip */
                }
              });
            }
          } catch {
            /* skip */
          }
        }
      }
    }

    const normalize = (s) => (s || "").trim();
    const isValid = (s) =>
      s && s.trim() && s.trim().toLowerCase() !== "null";
    const ccList = (complianceChecksList ?? []).filter((cc) => {
      if (cc?.inspectedDoorId !== door?.inspectedDoorId) return false;
      if (!cc?.isCompliant) return true;
      return (
        showWhenCompliant?.includes(normalize(cc?.checkListName)) &&
        isValid(cc?.severity)
      );
    });

    ccList.forEach((cc) => {
      const name = (cc?.checkListName || "").trim();
      if (hideWhenNonCompliant.includes(name) && !cc?.isCompliant) return;

      const severity =
        cc?.severity && cc.severity.trim() ? cc.severity : "Low";
      const actionText = resolveActionRequired(
        cc?.remediation,
        cc?.comment,
        cc?.category,
        cc?.severity,
      );

      if (actionText && actionText.trim()) {
        actionItems.push({
          door,
          title: name,
          severity,
          actionRequired: actionText,
          remediation: cc?.remediation,
          comment: cc?.comment,
          dueDate: cc?.dueDate,
          completedOn: cc?.completedOn,
          completedBy: cc?.completedBy || "",
          assignedTo: cc?.assignedTo || "",
        });
      }
    });
  });

  return actionItems;
}

export function getActionPlanPageCount(actionItems) {
  const rowsPerPageFirst = 5;
  const rowsPerPageSubsequent = 6;
  const totalRows = actionItems.length;
  if (totalRows === 0) return 0;
  const remainingRows = Math.max(0, totalRows - rowsPerPageFirst);
  return (
    1 +
    (remainingRows > 0
      ? Math.ceil(remainingRows / rowsPerPageSubsequent)
      : 0)
  );
}
